import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'antd';
import { FiSend, FiArrowLeft, FiImage, FiMessageSquare } from 'react-icons/fi';
import { Notification } from 'services';
import { initialsOf } from './participantsPanel';
import emptyImage from '../../../Assets/schedule/Group 10647.svg';
import '../broadcastNext.css';

// The same illustration the Resources tab uses for "nothing here yet", so an empty chat reads
// as a state of the room rather than as something that failed to load.
const EmptyState = ({ note }) => (
  <div className="bn-empty bn-empty-illustrated">
    <img src={emptyImage} alt="" className="bn-empty-art" />
    <div className="bn-empty-note">{note}</div>
  </div>
);

const two = (value) => `${value}`.padStart(2, '0');

const clockOf = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${two(date.getHours())}:${two(date.getMinutes())}`;
};

// "10:04" today, "Yesterday", then the date.
const stampOf = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return clockOf(date);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return `${date.getDate()}/${date.getMonth() + 1}`;
};

const Bubble = ({ message, mine }) => (
  <div className={`bn-message ${mine ? 'bn-mine' : ''}`}>
    {!mine ? (
      <div className="bn-message-meta">
        {message.user?.name ?? 'Participant'} · {clockOf(message.createdAt)}
      </div>
    ) : null}
    {message.type === 'Image' && message.image ? (
      // A shared image is unreadable at bubble width, so it opens full size on click. antd's
      // own viewer rather than a hand-rolled one: it brings zoom, rotate, Esc and the arrow
      // keys, and the PreviewGroup around the list means the arrows walk the conversation's
      // other images instead of stopping at the one that was clicked.
      <Image
        className="bn-bubble-image"
        wrapperClassName="bn-bubble-image-wrap"
        src={message.image}
        alt=""
      />
    ) : (
      <div className="bn-bubble">{message.text}</div>
    )}
  </div>
);

// History from joinClass() and messages arriving live are two different sources appended to
// one array, and only the live half is guaranteed to be in order. The old RightColumn read
// the same state through a .slice().reverse(), so the two panels disagreed about which way
// round it is - order it by its own timestamps instead of trusting either convention, which
// is right whichever way the API returns it. Stable, so equal stamps keep arrival order.
const inTimeOrder = (messages) => {
  if (!messages?.length) return messages;
  const stamp = (message) => {
    const value = new Date(message?.createdAt).getTime();
    return Number.isNaN(value) ? 0 : value;
  };
  return messages
    .map((message, index) => ({ message, index }))
    .sort((a, b) => stamp(a.message) - stamp(b.message) || a.index - b.index)
    .map((entry) => entry.message);
};

const MessageList = ({ messages: unordered, selfId, emptyNote }) => {
  const endRef = useRef(null);
  const messages = useMemo(() => inTimeOrder(unordered), [unordered]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages?.length]);

  if (!messages?.length) {
    return (
      <div className="bn-messages">
        <EmptyState note={emptyNote} />
      </div>
    );
  }
  return (
    <div className="bn-messages">
      {/* Every image in this conversation is one preview group, so opening any of them and
          pressing the arrows walks the rest. Wrapping the list rather than each bubble is
          what puts them in the same group - it works through context, and the bubbles that
          hold no image simply never register. */}
      <Image.PreviewGroup>
        {messages.map((message, index) => (
          <Bubble
            key={message.id ?? index}
            message={message}
            mine={`${message.user?.id ?? message.userId}` === `${selfId}`}
          />
        ))}
      </Image.PreviewGroup>
      <div ref={endRef} />
    </div>
  );
};

const Composer = ({ placeholder, onSend, onSendImage, disabled }) => {
  const [text, setText] = useState('');
  const fileRef = useRef(null);
  const hasText = !!text.trim();

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText('');
  };

  // Same two limits the old chat enforced, kept here so both the class and the lobby get
  // the message the moment the file is chosen rather than after a failed upload.
  const pickImage = (event) => {
    const file = event.target.files?.[0];
    // Clear the input before anything else: it holds the chosen path, and without this
    // picking the same picture twice in a row fires no second change event.
    event.target.value = '';
    if (!file) return;
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      Notification.error('error', 'You can only upload JPG/PNG file!');
      return;
    }
    if (file.size / 1024 / 1024 >= 2) {
      Notification.error('error', 'Image must smaller than 2MB!');
      return;
    }
    onSendImage(file);
  };

  return (
    <div className="bn-composer">
      <input
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
      />
      {/* One button, showing whichever send the composer is actually able to do: an empty
          box can only send a picture, a typed one can only send the text. Sending clears
          the box, which puts the picture button back. */}
      {hasText || !onSendImage ? (
        <button type="button" className="bn-send" onClick={submit} disabled={disabled} title="Send">
          <FiSend size={16} />
        </button>
      ) : (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            hidden
            onChange={pickImage}
          />
          <button
            type="button"
            className="bn-send"
            onClick={() => fileRef.current?.click()}
            disabled={disabled}
            title="Send a picture"
          >
            <FiImage size={16} />
          </button>
        </>
      )}
    </div>
  );
};

/**
 * Chat tab of the side panel: group discussion and private threads.
 * Sending goes through the same chatClient calls the old RightColumn used, and every message
 * shown comes from the existing redux state — no new endpoints.
 */
const ChatPanel = ({
  mode = 'group',
  onModeChange,
  selfId,
  groupMessages = [],
  threads = [],
  activeThread = null,
  threadMessages = [],
  onOpenThread,
  onCloseThread,
  onSendGroup,
  onSendPrivate,
  onSendGroupImage,
  onSendPrivateImage,
  canSend = true,
  // Unread as the room counts it. Only ever drawn on the side that is not on screen:
  // a dot beside the conversation being read says nothing, and the count behind it keeps
  // climbing while it is read because nothing marks the same message read twice.
  groupUnread = 0,
  privateUnread = 0,
}) => {
  const sortedThreads = useMemo(
    () =>
      [...threads].sort((a, b) => {
        const left = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
        const right = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
        return right - left;
      }),
    [threads],
  );

  // An open private thread replaces the list with the conversation.
  if (mode === 'private' && activeThread) {
    return (
      <>
        <div className="bn-thread-head">
          <button type="button" className="bn-back" onClick={onCloseThread}>
            <FiArrowLeft size={14} style={{ verticalAlign: 'middle' }} /> Back
          </button>
          <div className="bn-thread-head-avatar">{initialsOf(activeThread.name)}</div>
          <div className="bn-thread-name">{activeThread.name}</div>
        </div>
        <MessageList
          messages={threadMessages}
          selfId={selfId}
          emptyNote={`No messages with ${activeThread.name} yet.`}
        />
        <Composer
          placeholder={`Message ${activeThread.name?.split(' ')[0] ?? ''}`}
          onSend={onSendPrivate}
          onSendImage={onSendPrivateImage}
          disabled={!canSend}
        />
      </>
    );
  }

  return (
    <>
      <div className="bn-chat-modes">
        <button
          type="button"
          className={`bn-chat-mode ${mode === 'group' ? 'bn-on' : ''}`}
          onClick={() => onModeChange('group')}
        >
          Group
          {mode !== 'group' && groupUnread > 0 ? <span className="bn-unread-dot" /> : null}
        </button>
        <button
          type="button"
          className={`bn-chat-mode ${mode === 'private' ? 'bn-on' : ''}`}
          onClick={() => onModeChange('private')}
        >
          Private
          {/* The thread rows carry their own counts, so this only has to say that
              something is waiting on the side that cannot be seen from here. */}
          {mode !== 'private' && privateUnread > 0 ? <span className="bn-unread-dot" /> : null}
        </button>
      </div>

      {mode === 'group' ? (
        <>
          <MessageList
            messages={groupMessages}
            selfId={selfId}
            emptyNote="No messages yet. Say Hello to the class."
          />
          <Composer
            placeholder="Message everyone"
            onSend={onSendGroup}
            onSendImage={onSendGroupImage}
            disabled={!canSend}
          />
        </>
      ) : (
        <div className="bn-threads">
          {sortedThreads.length ? (
            sortedThreads.map((thread) => (
              <div className="bn-thread" key={thread.id} onClick={() => onOpenThread(thread)}>
                <div className="bn-person-avatar-wrap">
                  <div className="bn-thread-avatar">
                    {thread.image ? <img src={thread.image} alt="" /> : initialsOf(thread.name)}
                  </div>
                  {/* No dot at all when the room has told us nothing about this person -
                      only a present green or an absent red, never a guess. */}
                  {thread.online === undefined ? null : (
                    <span className={`bn-presence ${thread.online ? '' : 'bn-idle'}`} />
                  )}
                </div>
                <div className="bn-person-main">
                  <div className="bn-thread-top">
                    <div className="bn-thread-name">
                      {thread.name}
                      {thread.role && thread.role !== 'Student' ? ` (${thread.role})` : ''}
                    </div>
                    <div className="bn-thread-time">{stampOf(thread.lastMessageTime)}</div>
                  </div>
                  <div className="bn-thread-preview">{thread.lastMessage || 'No messages yet'}</div>
                </div>
                {thread.unread ? <span className="bn-unread">{thread.unread}</span> : null}
                <button
                  type="button"
                  className="bn-row-btn"
                  title={`Message ${thread.name}`}
                  style={{ marginLeft: 8 }}
                >
                  <FiMessageSquare size={16} />
                </button>
              </div>
            ))
          ) : (
            <EmptyState note="No private conversations yet. Open one from the Participants tab." />
          )}
        </div>
      )}
    </>
  );
};

export default ChatPanel;
